import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogTitle, TextField } from '@mui/material';
import { useSnackbarStore } from 'store/useSnackbarStore';
import SnackbarAlert from 'components/SnackbarAlert';
import { useTranslation } from 'react-i18next';

interface VisitRequestButtonProps {
  status: string;
  isManager: boolean;

  onChange: () => void; // 부모에서 전달받은 함수
  onCancel: () => void; // 부모에서 전달받은 함수
  onApprove: () => void; // 부모에서 전달받은 함수
  onReject: (reason: string) => void; // 부모에서 전달받은 함수
}

const VisitRequestBtn = ({ status, isManager, onChange, onCancel, onApprove, onReject }: VisitRequestButtonProps) => {
  const [confirmPopupOpen, setConfirmPopupOpen] = useState<boolean>(false);
  const [action, setAction] = useState<(() => void) | null>(null);
  const [dialogText, setDialogText] = useState<string>(''); // State for dialog text
  const [rejectedReason, setRejectedReason] = useState<string>('');
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar); //Alert zustand 상태 관리
  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용

  const handleConfirmPopupOpen = (action: () => void, text: string) => {
    setAction(() => action); // 변경|취소|승인|반려에 해당하는 로직으로 변경
    setDialogText(text); // 변경|취소|승인|반려 텍스트 변경
    setConfirmPopupOpen(true); // 한번 더 컨펌하는 팝업 열음
  };
  const handleConfirmPopupClose = () => {
    // setRejectedReason(''); // 반려사유 초기화
    setConfirmPopupOpen(false); // 팝업 닫기
  };

  const handleRejectedReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const reason = event.target.value;
    setRejectedReason(reason);
    resetAction(() => onReject(reason));
  };
  const resetAction = (action: () => void) => {
    setAction(() => action);
  };

  const handleConfirm = () => {
    if (dialogText === '반려' && rejectedReason === '') {
      alert('반려 사유를 입력해주세요.');
      return;
    }
    // TODO : 액션 이후 상태보고 Alert띄워여ㅑ함
    if (action) {
      action();
    } else {
      return;
    }
    handleConfirmPopupClose();
    setDialogText(''); // 버튼 텍스트 초기화
    // setRejectedReason(''); // 반려사유 초기화
    // navigate('/visit/history'); //내방신청내역으로 이동
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const renderButtons = () => {
    switch (status) {
      case 'REQUESTED':
        return (
          <>
            <Button
              variant="contained"
              color="success"
              size="medium"
              onClick={() => handleConfirmPopupOpen(onChange, '변경')} // 변경 처리 함수 호출
              sx={{ width: '100px', borderRadius: '4px', fontWeight: 'bold' }}
            >
              {t('visit_request.visitor_information_list.button_name.edit')}
            </Button>
            <Button
              variant="contained"
              color="error"
              size="medium"
              onClick={() => handleConfirmPopupOpen(onCancel, '신청취소')} // 취소 처리 함수 호출
              sx={{ width: '100px', borderRadius: '4px', fontWeight: 'bold' }}
            >
              {t('visit_request.visitor_information_list.button_name.cancel')}
            </Button>
          </>
        );
      case 'APPROVED':
      case 'REQUEST_COMPLETED':
      case 'REJECTED':
      case 'CANCELED':
        return null; // 아무 버튼도 표시하지 않음
    }
  };

  return (
    <>
      <SnackbarAlert />
      {isManager && (status === 'REQUESTED' || status === 'APPROVED' || status === 'REQUEST_COMPLETED' || status === 'REJECTED' || status === 'CANCELED') && (
        <>
          {/* 신청상태 */}
          {status === 'REQUESTED' && (
            <>
              <Button
                variant="outlined"
                color="success"
                size="medium"
                onClick={() => handleConfirmPopupOpen(onApprove, '승인')} // 승인 처리 함수 호출
                sx={{ width: '100px', borderRadius: '4px', fontWeight: 'bold' }}
              >
                {t('visit_request.visitor_information_list.button_name.approve')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                onClick={() => handleConfirmPopupOpen(() => onReject(rejectedReason), '반려')} // 반려 처리 함수 호출
                sx={{ width: '100px', borderRadius: '4px', fontWeight: 'bold' }}
              >
                {t('visit_request.visitor_information_list.button_name.reject')}
              </Button>
            </>
          )}

          {/* 승인상태 */}
          {status === 'APPROVED' && (
            <>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                onClick={() => handleConfirmPopupOpen(() => onReject(rejectedReason), '반려')} // 반려 처리 함수 호출
                sx={{ width: '100px', borderRadius: '4px', fontWeight: 'bold' }}
              >
                {t('visit_request.visitor_information_list.button_name.reject')}
              </Button>
            </>
          )}
        </>
      )}
      {!isManager && renderButtons()}

      {/* Confirmation Dialog */}
      {confirmPopupOpen && dialogText &&
        <Dialog open={confirmPopupOpen} onClose={handleConfirmPopupClose}>
          <DialogTitle>{dialogText} 하시겠습니까?</DialogTitle>

          <DialogActions sx={{ display: 'flex', flexDirection: 'column' }}>
            {dialogText === '반려' && (
              <TextField
                label="반려사유"
                value={rejectedReason}
                onChange={handleRejectedReasonChange}
                sx={{ flexGrow: 1, width: '300px', height: '40px', margin: '20px 10px' }}
                type="text"
                // size="small"
                variant="outlined"
                InputProps={{
                  sx: {
                    height: '60px'
                  }
                }}
                InputLabelProps={{
                  shrink: true
                }}
              />
            )}
            <Box>
              <Button onClick={handleConfirmPopupClose} color="inherit">
                취소
              </Button>
              <Button onClick={handleConfirm} color="primary">
                확인
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      }
    </>
  );
};

export default VisitRequestBtn;
